import { Layout } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Activity, Droplets } from 'lucide-react';
import HeaderBar from 'Layout/HeaderBar';
import FooterBar from 'Layout/FooterBar';
import { functions } from '../../firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';

const { Content } = Layout;

// TypeScript interfaces for the data structure
interface Plant {
  module: string;
}

interface Level {
  device_id: string;
  org_id: string;
  org_title: string;
}

interface PlantWeekStatus {
  module: string;
  org_id: string;
  org_title: string;
  rows_in_week: number;
  has_data: boolean;
  binder_total_l_delta: number;
  vand_total_l_delta: number;
  had_alarm: boolean;
  first_ts_in_week: {
    value: string;
  };
  last_ts_in_week: {
    value: string;
  };
}

interface LevelWeekStatus {
  device_id: string;
  org_id: string;
  org_title: string;
  rows_in_week: number;
  has_data: boolean;
  water_delta_l: number;
  binder_cm_usage_cm: number;
  first_ts_in_week: {
    value: string;
  };
  last_ts_in_week: {
    value: string;
  };
}

interface UnifiedDevice {
  org_id: string;
  org_title: string;
  plants: Array<{
    id: string;
    status?: PlantWeekStatus;
  }>;
  levels: Array<{
    id: string;
    status?: LevelWeekStatus;
  }>;
}

interface SettingsData {
  plants: Plant[];
  levels: Level[];
  plantsWeekStatus: PlantWeekStatus[];
  levelWeekStatus: LevelWeekStatus[];
}

// API function to call the settings Firebase function
const getSettings = async () => {
  const functionRef = functions.httpsCallable('settings');
  const response: any = await functionRef({
    requestType: 'GET_GSM_PLANT_OVERVIEW',
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode && errorCode !== 200) {
    throw new Error(`Settings API error: ${errorCode}`);
  }

  return response.data;
};

function GsmPlantOverviewContainer() {
  const {
    data: settings,
    isLoading,
    error,
    isError,
  } = useQuery<SettingsData>({
    queryKey: ['settings'],
    queryFn: getSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Helper function to merge plants and levels by orgId
  const getUnifiedDevices = (): UnifiedDevice[] => {
    if (!settings) return [];

    const orgMap = new Map<string, UnifiedDevice>();

    // Process plants
    settings.plants.forEach((plant) => {
      const plantStatus = settings.plantsWeekStatus.find(
        (status) => status.module === plant.module
      );
      if (plantStatus?.org_id && plantStatus?.org_title) {
        const orgId = plantStatus.org_id;
        if (!orgMap.has(orgId)) {
          orgMap.set(orgId, {
            org_id: orgId,
            org_title: plantStatus.org_title,
            plants: [],
            levels: [],
          });
        }
        orgMap.get(orgId)!.plants.push({
          id: plant.module,
          status: plantStatus,
        });
      }
    });

    // Process levels
    settings.levels.forEach((level) => {
      const levelStatus = settings.levelWeekStatus.find(
        (status) => status.device_id === level.device_id
      );
      if (level.org_id && level.org_title) {
        const orgId = level.org_id;
        if (!orgMap.has(orgId)) {
          orgMap.set(orgId, {
            org_id: orgId,
            org_title: level.org_title,
            plants: [],
            levels: [],
          });
        }
        orgMap.get(orgId)!.levels.push({
          id: level.device_id,
          status: levelStatus,
        });
      }
    });

    return Array.from(orgMap.values()).sort((a, b) => a.org_title.localeCompare(b.org_title));
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to determine if organization has any alarms
  const hasAlarm = (device: UnifiedDevice): boolean => {
    return device.plants.some((plant) => plant.status?.had_alarm) || false;
  };

  console.log({ settings: settings?.plantsWeekStatus });

  return (
    <Layout>
      <HeaderBar />
      <Content
        style={{
          margin: '24px 24px 0',
          padding: '0 24px',
        }}
      >
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">Loading plants...</div>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-500 text-lg">Error loading plants: {error?.message}</div>
          </div>
        )}

        {settings && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">GSM anlæg & niveau sensorer - overblik</h1>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-sm">
                  {settings.plants.length} GSM anlæg (email data)
                </Badge>
                <Badge variant="outline" className="text-sm">
                  {settings.levels.length} Niveau sensorer
                </Badge>
              </div>
            </div>

            <div className="grid gap-1 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {getUnifiedDevices().map((org) => (
                <Card key={org.org_id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold">{org.org_title}</CardTitle>
                      {/* {hasAlarm(org) && (
                        <Badge variant="destructive" className="ml-2">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Alarm
                        </Badge>
                      )} */}
                      {/* {!hasAlarm(org) && org.plants.length > 0 && (
                        <Badge variant="default" className="ml-2">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          OK
                        </Badge>
                      )} */}
                    </div>
                    {/* <CardDescription className="text-sm text-gray-600">
                      {org.plants.length} GSM • {org.levels.length} Niveau
                    </CardDescription> */}
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Plants Section */}
                    {org.plants.length > 0 && (
                      <div className="space-y-3">
                        {org.plants.length > 0 && (
                          <div className="text-sm font-medium text-gray-700 border-b pb-1">GSM</div>
                        )}
                        {org.plants.map((plant) => (
                          <div key={plant.id} className="space-y-2 p-2 bg-gray-50 rounded">
                            <div className="flex flex-col items-start justify-between">
                              <div className="font-medium text-sm">{plant.id}</div>
                              {plant.status?.had_alarm && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Havde alarmer i ugen
                                </Badge>
                              )}
                            </div>
                            {plant.status && (
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div>
                                  <div className="text-gray-600">Data logs</div>
                                  <div className="font-semibold">{plant.status.rows_in_week}</div>
                                </div>
                                <div>
                                  <div className="text-gray-600">Vand (L)</div>
                                  <div className="font-semibold">
                                    {plant.status.vand_total_l_delta}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-600">Binder (L)</div>
                                  <div className="font-semibold">
                                    {plant.status.binder_total_l_delta}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Levels Section */}
                    {org.levels.length > 0 && (
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-gray-700 border-b pb-1">
                          Niveau sensor
                        </div>
                        {org.levels.map((level) => (
                          <div key={level.id} className="space-y-2 p-2 bg-blue-50 rounded">
                            <div className="font-medium text-sm">{level.id}</div>
                            {level.status && (
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div>
                                  <div className="text-gray-600">Data logs</div>
                                  <div className="font-semibold">{level.status.rows_in_week}</div>
                                </div>
                                <div>
                                  <div className="text-gray-600">Vand Δ (L)</div>
                                  <div className="font-semibold">{level.status.water_delta_l}</div>
                                </div>
                                <div>
                                  <div className="text-gray-600">Binder (cm)</div>
                                  <div className="font-semibold">
                                    {level.status.binder_cm_usage_cm.toFixed(1)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No Data Available */}
                    {org.plants.length === 0 && org.levels.length === 0 && (
                      <div className="text-center text-gray-500 py-4">Ingen data</div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </Content>
      <FooterBar />
    </Layout>
  );
}

export default GsmPlantOverviewContainer;
